import { ComboboxButton, ComboboxOption, ComboboxOptions, Field } from '@headlessui/react'
import {
  AmountBadge,
  ArrowButton,
  CloseButton,
  ComboboxMultiple,
  FieldErrorAndDescription,
  FieldLabel,
  FormSelectDataType,
  FormSelectIdType,
  FormSelectInput,
  FormSelectMultipleProps,
  Tooltip,
  getVirtualizedData,
} from '@systemeio/ui'
import React, { useEffect, useMemo, useState } from 'react'
import {
  ListOptionsAsync,
  ListOptionsAsyncProps,
} from 'shared/components/form-select/components/list-options-async'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'

interface FormSelectAsyncMultipleProps<T extends FormSelectIdType>
  extends FormSelectMultipleProps<T>,
    Pick<ListOptionsAsyncProps, 'isFetching' | 'moreItems'> {
  search: string
  onSearchChange: (search: string) => void
}

export function FormSelectAsyncMultiple<T extends FormSelectIdType>({
  label,
  data,
  staticData,
  onChange,
  labelClassName,
  value,
  isPreFetching,
  placeholder,
  error,
  required,
  description,
  className,
  containerClassName,
  closeButtonClassName,
  arrowButtonClassName,
  fieldClassName,
  disabled,
  withoutCloseIcon,
  onOpen,
  onClose,
  dataTest,
  wrapperProps,
  loaderClassName,
  loaderWrapperClassName,
  search,
  onSearchChange,
  isFetching,
  moreItems,
  inputWrapperClassName,
  inputRef,
  ...optionsProps
}: FormSelectAsyncMultipleProps<T>) {
  const { t } = useLocoTranslation()
  const [opened, setOpened] = useState(false)

  useEffect(() => {
    if (opened && onOpen) {
      onOpen()
      setOpened(false)
    }
  }, [opened])

  const selectedData = useMemo(
    () => (data ?? staticData)?.filter(el => value?.includes(el.id)) ?? [],
    [data, staticData, value],
  )

  const hasValue = value.length > 0

  const noData = !data && !staticData

  const onlyValue = hasValue && noData

  const isDisabled = disabled || isPreFetching || onlyValue

  const virtual = useMemo(() => {
    const virtualData = getVirtualizedData(data)
    if (!virtualData) return

    const MORE_ITEMS_KEY = '__moreItems-key'

    const hasMoreItems = virtualData.options.some(
      (el: FormSelectDataType<T>) => el.id === MORE_ITEMS_KEY,
    )

    if (moreItems && !isFetching && !hasMoreItems) {
      virtualData.options.push({
        id: MORE_ITEMS_KEY,
        caption: t('global.dynamic_search.has_more_results', {
          count: moreItems,
        }),
        className:
          'bg-background-secondary hover:bg-background-secondary cursor-default text-text-tertiary/70 hover:text-text-tertiary/70 text-xs',
        disabled: true,
      })
    }

    return virtualData
  }, [data, isFetching, moreItems, t])

  const showCloseButton = hasValue && !withoutCloseIcon && !isDisabled

  return (
    <div className={twMerge('relative flex flex-col gap-1', containerClassName)}>
      <ComboboxMultiple
        selectedData={selectedData}
        virtual={virtual}
        onChange={onChange}
        disabled={isDisabled}
        onClose={() => {
          onClose?.()
        }}
      >
        {open => (
          <>
            <Field className={twMerge('relative flex flex-col gap-1', fieldClassName)}>
              <FieldLabel
                wrapperAs={'label'}
                wrapperProps={wrapperProps}
                label={label}
                required={required}
                labelClassName={twMerge('text-sm font-medium', labelClassName)}
              />
              <ComboboxButton
                as="button"
                className={twMerge('relative', inputWrapperClassName)}
              >
                <FormSelectInput
                  dataTest={dataTest}
                  inputRef={inputRef}
                  isDisabled={isDisabled}
                  hasError={!!error}
                  open={open}
                  className={className}
                  placeholder={placeholder ?? t('global.dynamic_search.start_typing')}
                  selectedCaption={search}
                  isPreFetching={isPreFetching}
                  onFocus={() => setOpened(true)}
                  onChange={value => {
                    setOpened(true)
                    onSearchChange(value)
                  }}
                />
                <ArrowButton
                  isDisabled={isDisabled}
                  arrowButtonClassName={twMerge(
                    'absolute inset-y-0 right-4 h-[46px] sm:h-[42px]',
                    arrowButtonClassName,
                  )}
                  open={open}
                />
              </ComboboxButton>
              {showCloseButton && (
                <CloseButton
                  isDisabled={isDisabled}
                  closeButtonClassName={twMerge(
                    'absolute bottom-0 right-10 h-[46px] sm:h-[42px]',
                    closeButtonClassName,
                  )}
                  onClick={() => onChange([])}
                />
              )}
            </Field>
            <ListOptionsAsync
              asOptions={ComboboxOptions}
              asOption={ComboboxOption}
              moreItems={moreItems}
              isFetching={isFetching}
              data={data}
              {...optionsProps}
            />
          </>
        )}
      </ComboboxMultiple>
      <FieldErrorAndDescription error={error} description={description} />
      {selectedData.length > 0 && (
        <div className={'absolute right-0 top-0 -translate-y-1/2 translate-x-1/2'}>
          <Tooltip
            mode={'hover'}
            label={
              <ul className={'flex flex-col'}>
                {selectedData.map(el => (
                  <li key={el.id}>{el.caption}</li>
                ))}
              </ul>
            }
          >
            <AmountBadge amount={selectedData.length} />
          </Tooltip>
        </div>
      )}
    </div>
  )
}
